import { environments, type Environment } from "./constants";
import { getRoutingKeyFromCookie } from "./getRoutingKeyFromCookie";

/**
 * Hostname of the developer computer running the build.
 * This is swapped out for the actual value during the build process.
 */
declare let __DEV_HOSTNAME__: string | undefined;

const ipAddressExp = /^(?:\d{1,3}\.){3}\d{1,3}$/;
const previewExp = /^pr[a-zA-Z\-_]+\d+$/;
const communalExp = /^prcom\d+$/;

export { environments } from "./constants";
export type { Environment } from "./constants";

export type EnvironmentInfo = {
  environmentType: Environment;
  environmentKey?: string;
};

/**
 * Identify the JSQ deployment environment from a hostname, with `CYPRESS_CI`/
 * `NODE_ENV`/`__DEV_HOSTNAME__` build signals taking precedence. Uses
 * `window.location.hostname` by default.
 */
export function identifyEnvironment(
  hostname: string | undefined = window.location?.hostname,
): EnvironmentInfo {
  if (process.env.CYPRESS_CI) {
    return { environmentType: environments.circleCI };
  }

  const devHostname = typeof __DEV_HOSTNAME__ !== "undefined" ? __DEV_HOSTNAME__ : undefined;
  if (process.env.NODE_ENV === "development" && devHostname) {
    return { environmentType: environments.development, environmentKey: devHostname };
  }

  if (hostname === undefined) {
    return { environmentType: environments.unknown };
  }

  if (hostname === "localhost" || ipAddressExp.test(hostname)) {
    return { environmentType: environments.development };
  }

  const splitHostname = hostname.split(".");
  if (splitHostname.length < 3) {
    return { environmentType: environments.unknown };
  }

  const maybeEnv = splitHostname.at(-3);
  const isJunipersquareCom = splitHostname.slice(-2).join(".") === "junipersquare.com";

  switch (maybeEnv) {
    case "cit":
      return { environmentType: environments.cit };
    case "prodops":
      return { environmentType: environments.prodops };
    case "evals":
      return { environmentType: environments.evals };
    case "staging":
      return { environmentType: environments.staging };
    case "qa":
      return { environmentType: environments.qa };
    case "perf":
      return { environmentType: environments.perf };
    case "test":
      return { environmentType: environments.test };
    case "dev":
    case "local":
      return { environmentType: environments.development };
    default:
      break;
  }

  if (maybeEnv !== undefined && previewExp.test(maybeEnv)) {
    // preview urls have the format <subdomain>.pr-<prNumber>.junipersquare.us
    // ipe urls have the format <subdomain>.pr<servicePrefix><prNumber>.junipersquare.us
    // servicePrefix can include dashes but not numbers
    if (communalExp.test(maybeEnv)) {
      // communal urls (e.g. prcom2) are the shared remocal environment, distinct
      // from a PR-specific IPE preview. Each sandbox is identified by its own
      // routing key (sd-routing-key cookie); the shared host isn't a meaningful
      // key, so leave it undefined when the cookie is absent.
      return {
        environmentType: environments.communal,
        environmentKey: getRoutingKeyFromCookie(),
      };
    }
    return { environmentType: environments.preview, environmentKey: maybeEnv };
  }

  if (splitHostname.length === 3 && isJunipersquareCom) {
    return { environmentType: environments.production };
  }

  return { environmentType: environments.unknown };
}
